import React, { useContext, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import AdminContext from "../../../StateContext/Application";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jwtDecode } from "jwt-decode";
import GooglePagination from "../../../styles/GooglePagination";

const DATE_COLUMNS = [
  // "Bill received to",
  "Bill Received",
  "Bill Date",
  "Work Order Date",
  "Work Order End Date",
  "Actual Work End Date",
  "GR/GO Date",
];

const formatExcelDate = (value) => {
  if (!value) return "";

  // Excel serial number
  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);

    if (parsed) {
      const dd = String(parsed.d).padStart(2, "0");
      const mm = String(parsed.m).padStart(2, "0");
      const yyyy = parsed.y;

      return `${dd}-${mm}-${yyyy}`;
    }
  }

  // JS Date object
  if (value instanceof Date) {
    const dd = String(value.getDate()).padStart(2, "0");
    const mm = String(value.getMonth() + 1).padStart(2, "0");
    const yyyy = value.getFullYear();

    return `${dd}-${mm}-${yyyy}`;
  }

  const str = String(value).trim();

  // already DD-MM-YYYY
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    return str;
  }

  // DD/MM/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str)) {
    const [d, m, y] = str.split("/");

    return `${d.padStart(2, "0")}-${m.padStart(2, "0")}-${y}`;
  }

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split("-");

    return `${d}-${m}-${y}`;
  }

  return str;
};

// ── ALL columns that MUST exist in the uploaded Excel ───────────────────────
// District is now a required per-row column
const EXPECTED_COLUMNS = [
  "Sr. No.",
  "Demand No.",
  "District", // ← NEW: per-row district
  "A.A.Year",
  "Bill Amount",
  // "Bill received to",
  "Bill Received",
  "Group",
  "Work Name",
  "Taluka",
  //   "Bill Copy",
  //   "Work Copy",
  "HeadCode",
  "Bill Date",
  "Work Order Date",
  "Work Order End Date",
  "Actual Work End Date",
  "Govt Resolution / Order No.",
  "GR/GO Date",
  "A.A. No.",
  "Beneficiary Name",
  "PAN No.",
  "Account Number",
  "Deduction",
  "Net Amount",
];

// ── Columns shown in the preview table ──────────────────────────────────────
const PREVIEW_COLUMNS = [
  "Sr. No.",
  "Demand No.",
  "District",
  "Work Name",
  "A.A.Year",
  "Bill Amount",
  "Deduction",
  "Net Amount",
  "Beneficiary Name",
  "Account Number",
  "Taluka",
];

// const BILLS_PER_PAGE = 10;

const OldBillUpload = () => {
  const [showAll, setShowAll] = useState(false);
  const [userData, setUserData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const BILLS_PER_PAGE = showAll ? filteredBills.length || 1 : 10;
  const operation = useContext(AdminContext);
  const { uploadOldBills } = operation;

  // ── decode JWT ────────────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("Token");
    if (token) {
      try {
        setUserData(jwtDecode(token));
      } catch (e) {
        console.error("Invalid token:", e);
      }
    }
  }, []);

  // ── pagination helpers ────────────────────────────────────────────────────
  const totalPages = Math.ceil(filteredBills.length / BILLS_PER_PAGE);
  const indexOfLast = currentPage * BILLS_PER_PAGE;
  const indexOfFirst = indexOfLast - BILLS_PER_PAGE;
  const currentBills = filteredBills.slice(indexOfFirst, indexOfLast);

  // ── FILE UPLOAD ───────────────────────────────────────────────────────────
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      // Read all rows as array-of-arrays so we can find the real header row
      const rawRows = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: "",
      });

      // Find the header row — it must contain "Sr. No."
      const headerRowIndex = rawRows.findIndex((row) =>
        row.some((cell) => String(cell).trim() === "Sr. No."),
      );
      if (headerRowIndex === -1) {
        alert('Could not find a header row containing "Sr. No." in this file.');
        return;
      }

      const headers = rawRows[headerRowIndex].map((h) => String(h).trim());

      // Check all required columns are present
      const missing = EXPECTED_COLUMNS.filter((c) => !headers.includes(c));
      if (missing.length > 0) {
        alert("Missing Columns:\n" + missing.join(", "));
        return;
      }

      // Convert data rows → objects; skip summary/blank rows
      const dataRows = rawRows
        .slice(headerRowIndex + 1)
        .filter((row) => {
          const srNo = row[headers.indexOf("Sr. No.")];
          return srNo !== "" && !isNaN(Number(srNo)) && Number(srNo) > 0;
        })
        .map((row) => {
          const obj = {};
          headers.forEach((h, i) => {
            let value = row[i] ?? "";

            if (h === "Account Number") {
              value = String(value);

              if (value.includes("E+")) {
                value = Number(value).toLocaleString("fullwide", {
                  useGrouping: false,
                });
              }
            }

            if (DATE_COLUMNS.includes(h)) {
              value = formatExcelDate(value);
            }

            obj[h] = value;
          });
          return obj;
        });

      if (dataRows.length === 0) {
        alert("No valid data rows found in the file.");
        return;
      }

      // Warn if any row is missing District
      const missingDistrict = dataRows.filter(
        (r) => !String(r["District"] || "").trim(),
      );
      if (missingDistrict.length > 0) {
        alert(
          `Warning: ${missingDistrict.length} row(s) have an empty "District" column.\n` +
            `Please fill the District for every row before uploading.`,
        );
        return;
      }

      // ── Amount columns must be numeric only (no text/symbols) ─────────────────
const MONEY_COLUMNS = ["Bill Amount", "Deduction", "Net Amount"];
const isValidAmount = (val) =>
  val !== null && val !== undefined && val !== "" &&
  /^\d+(\.\d+)?$/.test(String(val).trim());

const invalidAmountRows = dataRows.filter((row) =>
  MONEY_COLUMNS.some((col) => !isValidAmount(row[col])),
);

if (invalidAmountRows.length > 0) {
  const details = invalidAmountRows
    .map((r) => {
      const badCols = MONEY_COLUMNS.filter((c) => !isValidAmount(r[c]))
        .map((c) => `${c}="${r[c]}"`)
        .join(", ");
      return `Sr. No. ${r["Sr. No."]} (${r["Work Name"] || "N/A"}): ${badCols}`;
    })
    .join("\n");

  alert(
    `Bill Amount, Deduction and Net Amount must contain numbers only.\n\nCheck these rows:\n${details}`,
  );
  return;
}
// ────────────────────────────────────────────────────────────────────────


      setTableData(dataRows);
      setFilteredBills(dataRows);
      setCurrentPage(1);
    };

    reader.readAsBinaryString(file);
  };

  // ── SUBMIT ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!tableData.length) {
      alert("No data found. Please upload a file first.");
      return;
    }
    try {
      setLoading(true);
      const response = await uploadOldBills(tableData);
      if (response?.status) {
        alert(response.message || "Bills Uploaded Successfully");
        setTableData([]);
        setFilteredBills([]);
      } else {
        // If backend returned per-row errors, show them
        const errMsg = response?.errors?.length
          ? response.errors.join("\n")
          : response?.message || "Upload Failed";
        alert(errMsg);
      }
    } catch (err) {
      console.error(err);
      alert("Upload Failed");
    } finally {
      setLoading(false);
    }
  };

  // ── DOWNLOAD SAMPLE ───────────────────────────────────────────────────────
  const downloadSampleExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("PendingBills");

    worksheet.columns = EXPECTED_COLUMNS.map((col) => ({
      header: col,
      key: col,
      width: 30,
    }));

    const accountColIndex = EXPECTED_COLUMNS.indexOf("Account Number") + 1;

    worksheet.getColumn(accountColIndex).numFmt = "@";
    // Highlight the District column header in yellow so users notice it
    const districtCol = worksheet.getColumn("District");
    districtCol.header = "District";
    worksheet.getRow(1).getCell(EXPECTED_COLUMNS.indexOf("District") + 1).fill =
      {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFF00" },
      };

    worksheet.addRow({
      "Sr. No.": 1,
      "Demand No.": "SAT1125260050969",
      District: "नाशिक", // ← per-row district
      "A.A.Year": "2023-2024",
      "Bill Amount": 1561623,
      // "Bill received to": "28-03-2026",
      "Bill Received": "28-03-2026",
      Group: "ब",
      "Work Name": "विशेष दुरूस्ती शेरेची वाडी ते वाठार निं. रस्ता",
      Taluka: "फलटण",
      //   "Bill Copy":                    "bill_sample.pdf",
      //   "Work Copy":                    "work_sample.pdf",
      HeadCode: "3054-2419(Roads and bridge repair maintenance program)",
      "Bill Date": "27-03-2026",
      "Work Order Date": "22-02-2024",
      "Work Order End Date": "19-08-2024",
      "Actual Work End Date": "04-11-2025",
      "Govt Resolution / Order No.": "GR-No-2023",
      "GR/GO Date": "27-10-2023",
      "A.A. No.": "जिपर-2723/प्र.क्र.308/बांध-3",
      "Beneficiary Name": "DATTATRAY KHASHABA BOBADE",
      "PAN No.": "ADIPB3204E",
      "Account Number": "1015005001755",
      Deduction: 200094,
      "Net Amount": 1361529,
    });

    // Second sample row with a different district
    worksheet.addRow({
      "Sr. No.": 2,
      "Demand No.": "SAT1125260051421",
      District: "पुणे", // ← different district
      "A.A.Year": "2023-2024",
      "Bill Amount": 1539199,
      // "Bill received to": "31-03-2026",
      "Bill Received": "31-03-2026",
      Group: "ब",
      "Work Name": "प्रजिमा-22 ते राउतवाडी गणेशखिंड रस्ता",
      Taluka: "वाई",
      //   "Bill Copy":                    "bill_sample2.pdf",
      //   "Work Copy":                    "work_sample2.pdf",
      HeadCode: "2515-1238(MLA Representative)",
      "Bill Date": "31-03-2026",
      "Work Order Date": "03-11-2024",
      "Work Order End Date": "09-06-2024",
      "Actual Work End Date": "31-03-2026",
      "Govt Resolution / Order No.": "GR-No-2023",
      "GR/GO Date": "27-10-2023",
      "A.A. No.": "जिपर-2723/प्र.क्र.308/बांध-3",
      "Beneficiary Name": "BHOLENATH MAJUR SAH SAN PHALTAN",
      "PAN No.": "AAAAB9036G",
      "Account Number": "1001008000625",
      Deduction: 177886,
      "Net Amount": 1361313,
    });

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;

      const cell = row.getCell(accountColIndex);

      cell.value = String(cell.value || "");
      cell.numFmt = "@";
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "Pending_Bills_Sample.xlsx");
  };

  // ── RENDER ────────────────────────────────────────────────────────────────
  const isMoney = (col) =>
    ["Bill Amount", "Deduction", "Net Amount"].includes(col);

  return (
    <div
      style={{
        padding: "20px",
        height: "89vh",
        backgroundColor: "#EFF3F8",
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
        }}
      >
        <h4 style={{ marginBottom: "4px" }}>Upload Pending Bill Excel</h4>

        {/* ── LOADER ── */}
        {loading && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(255,255,255,0.6)",
              backdropFilter: "blur(4px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                border: "6px solid #eee",
                borderTop: "6px solid #007bff",
                borderRadius: "50%",
                width: "60px",
                height: "60px",
                animation: "spin 1s linear infinite",
              }}
            />
          </div>
        )}

        {/* ── SAMPLE DOWNLOAD ── */}
        <div style={{ marginBottom: "10px" }}>
          <span
            onClick={downloadSampleExcel}
            style={{ color: "#007bff", cursor: "pointer", fontWeight: "500" }}
          >
            Download Sample Excel
          </span>
        </div>

        {/* ── FILE INPUT ── */}
        <div style={{ marginBottom: "15px" }}>
          <input
            type="file"
            accept=".xls,.xlsx"
            onChange={handleFileUpload}
            className="form-control"
          />
        </div>

        {/* ── SUMMARY BADGES ── */}
        {tableData.length > 0 && (
          <>
            <div
              style={{
                display: "flex",
                gap: "12px",
                marginBottom: "12px",
                flexWrap: "wrap",
              }}
            >
              {[
                { label: "Total Bills", value: tableData.length },
                {
                  label: "Districts",
                  value: [...new Set(tableData.map((r) => r["District"]))].join(
                    ", ",
                  ),
                },
                {
                  label: "Total Bill Amount",
                  value: `₹${tableData.reduce((s, r) => s + Number(r["Bill Amount"] || 0), 0).toLocaleString("en-IN")}`,
                },
                {
                  label: "Total Deduction",
                  value: `₹${tableData.reduce((s, r) => s + Number(r["Deduction"] || 0), 0).toLocaleString("en-IN")}`,
                },
                {
                  label: "Total Net Amount",
                  value: `₹${tableData.reduce((s, r) => s + Number(r["Net Amount"] || 0), 0).toLocaleString("en-IN")}`,
                },
              ].map((b) => (
                <div
                  key={b.label}
                  style={{
                    background: "#f0f4ff",
                    border: "1px solid #c8d6f5",
                    borderRadius: "6px",
                    padding: "8px 14px",
                    fontSize: "13px",
                  }}
                >
                  <div style={{ color: "#555", marginBottom: "2px" }}>
                    {b.label}
                  </div>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "14px",
                      maxWidth: "300px",
                      wordBreak: "break-word",
                    }}
                  >
                    {b.value}
                  </div>
                </div>
              ))}
            </div>

            {/* ── TABLE PREVIEW ── */}
            <div
              style={{
                marginTop: "10px",
                borderRadius: "6px",
                overflow: "hidden",
                border: "1px solid #ccc",
              }}
            >
              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table
                  style={{
                    borderCollapse: "collapse",
                    width: "100%",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f5f7fa" }}>
                      {PREVIEW_COLUMNS.map((col) => (
                        <th
                          key={col}
                          style={{
                            padding: "8px 10px",
                            border: "1px solid #ddd",
                            whiteSpace: "nowrap",
                            textAlign: "left",
                            background:
                              col === "District" ? "#fff8dc" : undefined,
                          }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentBills.map((row, idx) => (
                      <tr
                        key={idx}
                        style={{
                          background: idx % 2 === 0 ? "#fff" : "#fafafa",
                        }}
                      >
                        {PREVIEW_COLUMNS.map((col) => (
                          <td
                            key={col}
                            style={{
                              padding: "6px 10px",
                              border: "1px solid #ddd",
                              whiteSpace: "nowrap",
                              background:
                                col === "District" ? "#fffde7" : undefined,
                            }}
                          >
                            {isMoney(col)
                              ? `₹${Number(row[col] || 0).toLocaleString("en-IN")}`
                              : String(row[col] ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── PAGINATION ── */}
            <div
              style={{
                marginTop: "15px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: "13px", color: "#555" }}>
                Showing {indexOfFirst + 1} to{" "}
                {Math.min(indexOfLast, filteredBills.length)} of{" "}
                {filteredBills.length} entries
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <GooglePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />

                <button
                  onClick={() => {
                    setShowAll(!showAll);
                    setCurrentPage(1);
                  }}
                  style={{
                    border: "none",
                    outline: "none",
                    cursor: "pointer",
                    padding: "8px 16px",
                    borderRadius: "20px",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#fff",
                    background: showAll
                      ? "linear-gradient(135deg, #28a745, #20c997)"
                      : "linear-gradient(135deg, #0067ab, #0098d5)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    transition: "all 0.3s ease",
                  }}
                >
                  {showAll ? "✓ Showing All" : "View All"}
                </button>
              </div>
            </div>

            {/* ── SUBMIT ── */}
            <div style={{ textAlign: "right", marginTop: "20px" }}>
              <button
                onClick={handleSubmit}
                style={{
                  background:
                    "linear-gradient(to right, #3b409e, #0067ab, #0098d5)",
                  color: "white",
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                Submit Bills
              </button>
            </div>
          </>
        )}
      </div>
      <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default OldBillUpload;
